import React, { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import * as THREE from 'three';

const PALETTE = {
  light: {
    inner: '#8B6344',
    mid: '#BA8759',
    outer: '#D4A574',
    stars: '#6B6258',
    opacity: 0.55,
  },
  dark: {
    inner: '#F0DCC8',
    mid: '#BA8759',
    outer: '#5A3D28',
    stars: '#E8D5C4',
    opacity: 0.75,
  },
};

/*
  Subtle 3D spiral-galaxy particle field rendered with three.js.
  Warm copper palette to match the portfolio. Slowly rotates and
  gently reacts to pointer movement. Purely decorative background.
*/
const GalaxyBackground = () => {
  const mountRef = useRef(null);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isDark =
      resolvedTheme === 'dark' ||
      (!resolvedTheme &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    const palette = isDark ? PALETTE.dark : PALETTE.light;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100);
    camera.position.set(0, 2.2, 6);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const COUNT = 7000;
    const ARMS = 4;
    const RADIUS = 5;
    const SPIN = 1.1;
    const RANDOMNESS = 0.55;
    const RANDOM_POWER = 2.4;

    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);

    const inner = new THREE.Color(palette.inner);
    const mid = new THREE.Color(palette.mid);
    const outer = new THREE.Color(palette.outer);

    for (let i = 0; i < COUNT; i++) {
      const i3 = i * 3;
      const radius = Math.pow(Math.random(), 1.5) * RADIUS;
      const branchAngle = ((i % ARMS) / ARMS) * Math.PI * 2;
      const spinAngle = radius * SPIN;

      const rand = () =>
        Math.pow(Math.random(), RANDOM_POWER) *
        (Math.random() < 0.5 ? 1 : -1) *
        RANDOMNESS *
        radius;

      const rx = rand();
      const ry = rand() * 0.35;
      const rz = rand();

      positions[i3] = Math.cos(branchAngle + spinAngle) * radius + rx;
      positions[i3 + 1] = ry;
      positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * radius + rz;

      const mixed = inner.clone();
      const t = radius / RADIUS;
      if (t < 0.5) mixed.lerp(mid, t / 0.5);
      else {
        mixed.copy(mid);
        mixed.lerp(outer, (t - 0.5) / 0.5);
      }
      colors[i3] = mixed.r;
      colors[i3 + 1] = mixed.g;
      colors[i3 + 2] = mixed.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.035,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
      transparent: true,
      opacity: palette.opacity,
    });

    const galaxy = new THREE.Points(geometry, material);
    galaxy.rotation.x = 0.35;
    galaxy.position.x = 2.2;
    scene.add(galaxy);

    const starCount = 900;
    const starPos = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const i3 = i * 3;
      starPos[i3] = (Math.random() - 0.5) * 40;
      starPos[i3 + 1] = (Math.random() - 0.5) * 40;
      starPos[i3 + 2] = (Math.random() - 0.5) * 40;
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    const starMat = new THREE.PointsMaterial({
      size: 0.03,
      color: new THREE.Color(palette.stars),
      transparent: true,
      opacity: isDark ? 0.5 : 0.35,
      depthWrite: false,
    });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    const pointer = { x: 0, y: 0 };
    const onMove = (e) => {
      pointer.x = (e.clientX / window.innerWidth - 0.5) * 2;
      pointer.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    const clock = new THREE.Clock();
    let frameId;
    const animate = () => {
      const elapsed = clock.getElapsedTime();
      galaxy.rotation.y = elapsed * (prefersReduced ? 0 : 0.06);
      stars.rotation.y = elapsed * 0.01;

      camera.position.x += (pointer.x * 0.6 - camera.position.x) * 0.03;
      camera.position.y += (2.2 - pointer.y * 0.4 - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      material.dispose();
      starGeo.dispose();
      starMat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [resolvedTheme]);

  return <div ref={mountRef} className="galaxy-bg" aria-hidden="true" />;
};

export default GalaxyBackground;
